import BorderContainer from "@/components/ContentContainer/BorderContainer";
import InputUnitConversion from "@/components/InputUnitConversion/InputUnitConversion";
import InputUnitConversionSkeleton from "@/components/InputUnitConversion/InputUnitConversionSkeleton";
import useAnalytics from "@/hooks/useAnalytics";
import useSmartQueryClient from "@/hooks/useSmartQueryClient";
import { Flex, message } from "antd";
import _ from "lodash";
import { useCallback, useMemo } from "react";
import {
  GetConsumableUnits,
  useGetConsumableUnits,
  useUpdateConsumableUnit,
} from "../useConsumables";

export default function ConsumableUnitConversion({ consumableId }: { consumableId: number }) {
  const updateUnit = useUpdateConsumableUnit();
  const { data, isLoading } = useGetConsumableUnits({ consumableId });
  const smartQueryClient = useSmartQueryClient();
  const analytics = useAnalytics();

  const saveUnit = useCallback((startUnit: string, targetUnit: string, factor: number | null) => {
    updateUnit
      .mutateAsync({
        consumableId,
        consumableUnit: { start_unit: startUnit, target_unit: targetUnit, factor },
      })
      .then(() => {
        analytics.track(`Consumable Unit Updated`);
      })
      .catch(() => {
        message.error("Something went wrong when updating consumable unit. Please try again.");
      });
  }, []);

  const debouncedSaveUnit = useMemo(() => _.debounce(saveUnit, 500), [saveUnit]);

  const onChangeUnit = (startUnit: string, targetUnit: string, factor: number | null) => {
    smartQueryClient.setQueryData<GetConsumableUnits>(
      ["consumables", "detail", consumableId, "units"],
      (consumableUnits) => {
        return consumableUnits
          ? consumableUnits.map((unit) => {
              if (unit.start_unit === startUnit && unit.target_unit === targetUnit) {
                return {
                  ...unit,
                  status: "validating",
                  factor: factor,
                };
              } else {
                return unit;
              }
            })
          : undefined;
      }
    );

    debouncedSaveUnit(startUnit, targetUnit, factor);
  };

  const isIncomplete = data?.some((x) => x.status === "incomplete");

  return (
    <BorderContainer incomplete={isIncomplete}>
      <Flex
        gap={16}
        vertical
      >
        {isLoading ? (
          <InputUnitConversionSkeleton />
        ) : data?.length === 0 ? (
          <div>No unit conversion needed.</div>
        ) : (
          data?.map((unit) => (
            <InputUnitConversion
              key={unit.start_unit + unit.target_unit}
              sourceUnit={unit.start_unit}
              targetUnit={unit.target_unit}
              onChange={(value) => {
                onChangeUnit(unit.start_unit, unit.target_unit, value);
              }}
              defaultValue={unit.factor || undefined}
              rootStatus={unit.status}
            />
          ))
        )}
      </Flex>
    </BorderContainer>
  );
}
